import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import styles from './styles';

export type Column = {
  value?: string | number;
  bold?: boolean;
  italic?: boolean;
  width?: number;
  leftBorder?: boolean;
};

interface TableRowProps {
  data: Record<string, Column>;
  backgroundColor?: string;
  fontColor?: string;
  isFirstRow?: boolean;
  alignContent?: 'left' | 'center' | 'right';
  fontSize?: number;
  fullBorder?: boolean;
  collapseFooterBorderBottomWhenEmptyValue?: boolean;
  isFooter?: boolean;
  expandRows?: boolean;
  tallRows?: boolean;
  footerRowHeight?: number;
  minHeight?: number;
  leftColumnColor?: string;
}

const TableRow: React.FC<TableRowProps> = ({
  data,
  backgroundColor,
  fontColor,
  isFirstRow,
  alignContent,
  fontSize,
  fullBorder,
  collapseFooterBorderBottomWhenEmptyValue,
  isFooter,
  expandRows,
  tallRows,
  footerRowHeight,
  minHeight,
  leftColumnColor,
}) => {
  const itemsCount = Object.keys(data).length;

  const fixedWidthSum = Object.values(data).reduce((width, column) => {
    return width + (column.width ?? 0);
  }, 0);
  const itemsWithFixedWidth = Object.values(data).filter(
    (column) => !!column.width,
  ).length;

  const autoWidth =
    itemsCount > itemsWithFixedWidth
      ? (100 - fixedWidthSum) / (itemsCount - itemsWithFixedWidth)
      : 0;

  // check if any of the data.property values are greater than 40
  const rowContainsLongText = Object.values(data).some(
    (column) => (column.value?.toString().length ?? 0) > 40,
  );

  return (
    <View
      style={[
        styles.tableRow,
        expandRows ? styles.expandingRow : styles.fixedRow,
        fullBorder ? styles.fullBorderRow : styles.partialBorderRow,
        (tallRows || rowContainsLongText) && !expandRows ? styles.tallRow : {},
        {
          backgroundColor,
          borderTopWidth: isFirstRow ? 1 : 0.5,
          borderBottomWidth: isFooter ? 0 : 0.5,
          ...(isFooter && footerRowHeight ? { height: footerRowHeight } : {}),
          ...(minHeight ? { minHeight } : {}),
        },
      ]}
      wrap={true}
    >
      {Object.entries(data).map(
        ([id, { value, bold, italic, width, leftBorder }], index) => (
          <TableColumn
            key={id}
            backgroundColor={
              index === 0 && leftColumnColor ? leftColumnColor : backgroundColor
            }
            fontColor={index === 0 && leftColumnColor ? '#FFFFFF' : fontColor}
            value={value}
            width={`${width ?? autoWidth}%`}
            bold={index === 0 && leftColumnColor ? true : bold}
            italic={italic}
            alignContent={alignContent}
            fontSize={fontSize}
            fullBorder={fullBorder}
            collapseFooterBorderBottomWhenEmptyValue={
              collapseFooterBorderBottomWhenEmptyValue
            }
            isFooter={isFooter}
            tallRows={tallRows}
            footerRowHeight={footerRowHeight}
            leftBorder={leftBorder}
          />
        ),
      )}
    </View>
  );
};

type TableColumnProps = {
  value?: Column['value'];
  backgroundColor?: string;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
  width: string;
  alignContent?: 'left' | 'center' | 'right';
  fontSize?: number;
  fullBorder?: boolean;
  collapseFooterBorderBottomWhenEmptyValue?: boolean;
  isFooter?: boolean;
  tallRows?: boolean;
  expandRows?: boolean;
  footerRowHeight?: number;
  leftBorder?: boolean;
};

const TableColumn: React.FC<TableColumnProps> = ({
  value,
  backgroundColor,
  fontColor,
  bold,
  italic,
  width,
  alignContent,
  fontSize,
  fullBorder,
  collapseFooterBorderBottomWhenEmptyValue = false,
  isFooter,
  tallRows,
  expandRows,
  footerRowHeight,
  leftBorder,
}) => {
  const isNotEmptyValue = value !== null && value !== undefined && value !== '';

  const baseStyles = [
    styles.tableCol,
    fullBorder &&
    (!isFooter || isNotEmptyValue || !collapseFooterBorderBottomWhenEmptyValue)
      ? styles.fullBorderCol
      : {},
    alignContent === 'center' ? styles.alignCenter : styles.alignLeft,
    tallRows && !expandRows ? styles.tallRowText : {},
  ];

  const dynamicStyles = {
    backgroundColor,
    color: fontColor,
    width,
    maxWidth: width,
    fontSize,
    borderBottomWidth:
      fullBorder &&
      isFooter &&
      (isNotEmptyValue || !collapseFooterBorderBottomWhenEmptyValue)
        ? 1
        : 0,
    ...(leftBorder ? { borderLeftWidth: 1 } : {}),
    ...(leftBorder
      ? { borderLeftColor: fullBorder ? '#333333' : '#979797' }
      : {}),
    ...(isFooter && footerRowHeight ? { paddingVertical: 1 } : {}),
  };

  const fontStyles = bold ? styles.bold : italic ? styles.italic : styles.text;

  const textStyle = [...baseStyles, dynamicStyles, fontStyles];

  return (
    <View wrap={false} style={textStyle}>
      {value?.toString().length ? (
        <Text
          style={
            alignContent === 'center'
              ? styles.tableCellValueCenter
              : styles.tableCellValueLeft
          }
          key={value.toString()}
        >
          {value.toString()}
        </Text>
      ) : null}
    </View>
  );
};

interface TableTitleProps {
  title: string;
  backgroundColor?: string;
  fontColor?: string;
}

const TableTitle: React.FC<TableTitleProps> = ({
  title,
  backgroundColor,
  fontColor,
}) => (
  <View
    style={[
      styles.tableRow,
      styles.tableTitleContainer,
      { backgroundColor, borderWidth: 1 },
    ]}
  >
    <Text style={[styles.tableTitle, styles.bold, { color: fontColor }]}>
      {title}
    </Text>
  </View>
);

const TableContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <View style={styles.table} wrap={true}>
    {children}
  </View>
);

interface TableProps {
  title?: string | null;
  columns?: Record<string, Column>;
  backgroundColor?: string;
  fontColor?: string;
  headerFontColor?: string;
  data: Array<Record<string, Column>>;
  alignContent?: 'left' | 'center' | 'right';
  footer?: Record<string, Column> | null;
  footerBackgroundColor?: string;
  footerFontColor?: string;
  fontSize?: number;
  fullBorder?: boolean;
  collapseFooterBorderBottomWhenEmptyValue?: boolean; // when true, footer cells with an empty value omit their bottom border
  expandRows?: boolean; // removes any height from the row which allows the row to expand to fit the content
  tallRows?: boolean; // adds a fixed height to the row, doesn't work if expandRows is true
  footerRowHeight?: number; // custom height for footer row
  headerMinHeight?: number; // custom min height for header row
  leftColumnColor?: string;
  rowsToKeepWithHeader?: number; // number of body rows to keep on the same page as the header
}

const Table: React.FC<TableProps> = ({
  title,
  columns,
  backgroundColor,
  fontColor,
  headerFontColor = fontColor,
  data,
  alignContent = 'center',
  footer,
  footerBackgroundColor,
  footerFontColor,
  fontSize = 7,
  fullBorder = false,
  collapseFooterBorderBottomWhenEmptyValue = false,
  expandRows = false,
  tallRows = false,
  footerRowHeight,
  headerMinHeight,
  rowsToKeepWithHeader,
  leftColumnColor,
}) => {
  const rowsKeptWithHeader =
    rowsToKeepWithHeader !== undefined
      ? data.slice(0, rowsToKeepWithHeader)
      : [];
  const remainingRows =
    rowsToKeepWithHeader !== undefined
      ? data.slice(rowsToKeepWithHeader)
      : data;

  const renderRow = (rowData: Record<string, Column>, rowId: number) => (
    <TableRow
      key={rowId}
      data={rowData}
      isFirstRow={false}
      alignContent={alignContent}
      fontSize={fontSize}
      fullBorder={fullBorder}
      expandRows={expandRows}
      tallRows={tallRows}
      leftColumnColor={leftColumnColor}
    />
  );

  const headerContent = (
    <>
      {title ? (
        <TableTitle
          title={title}
          backgroundColor={backgroundColor}
          fontColor={fontColor}
        />
      ) : null}
      {columns ? (
        <TableRow
          data={columns}
          backgroundColor={backgroundColor}
          fontColor={headerFontColor ?? fontColor}
          isFirstRow={!title}
          alignContent={alignContent}
          fontSize={fontSize}
          fullBorder={fullBorder}
          expandRows={expandRows}
          minHeight={headerMinHeight}
          leftColumnColor={leftColumnColor}
        />
      ) : null}
    </>
  );

  const footerContent = footer ? (
    <TableRow
      data={footer}
      backgroundColor={footerBackgroundColor ?? backgroundColor}
      fontColor={footerFontColor ?? fontColor}
      isFirstRow={false}
      alignContent={alignContent}
      fontSize={fontSize}
      fullBorder={fullBorder}
      collapseFooterBorderBottomWhenEmptyValue={
        collapseFooterBorderBottomWhenEmptyValue
      }
      expandRows={expandRows}
      isFooter
      footerRowHeight={footerRowHeight}
      leftColumnColor={leftColumnColor}
    />
  ) : null;

  if (rowsToKeepWithHeader !== undefined) {
    return (
      <TableContainer>
        <View wrap={false}>
          {headerContent}
          {rowsKeptWithHeader.map((rowData, rowId) =>
            renderRow(rowData, rowId),
          )}
        </View>
        {remainingRows.length > 0 && (
          <View wrap={true}>
            {remainingRows.map((rowData, rowId) =>
              renderRow(rowData, rowId + rowsToKeepWithHeader),
            )}
          </View>
        )}
        {footerContent}
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      {headerContent}
      {data.length
        ? data.map((rowData, rowId) => renderRow(rowData, rowId))
        : null}
      {footerContent}
    </TableContainer>
  );
};

export default Table;
